import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Smartphone, ShieldCheck, Globe, Megaphone, Wrench, Cloud, Layout, Code, FileText, Database, ShoppingCart, Apple, Layers, TrendingUp, Target, Share2, Zap, Headphones, Bug, ArrowUpCircle, CheckCircle, Clock, Smile, Server, Cpu, Tag, Puzzle, Users, Eye, ChevronRight, ArrowRight, PhoneCall, X, Gift, Sparkles, Settings2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import { Icon3DGlobe, Icon3DMobile, Icon3DMegaphone, Icon3DWrench, Icon3DCloud, Icon3DCode, Icon3DDomain, Icon3DEmail, Icon3DHosting, Icon3DWebDev, Icon3DMobileDev, Icon3DPartnership, Icon3DTransparency, Icon3DInnovation, Icon3DEfficiency, Icon3DFullStack, Icon3DUITraining, Icon3DBackendTraining } from '../components/3DIcon';

const Home = () => {
  const navigate = useNavigate();
  useTheme();
  const [activeService, setActiveService] = useState('Web');
  const [activeEnquiry, setActiveEnquiry] = useState('Domain');
  const [mobileNumber, setMobileNumber] = useState('');
  const [flippedCard, setFlippedCard] = useState<string | null>(null);
  const [callStatus, setCallStatus] = useState<string | null>(null);
  const [lastCalledNumber, setLastCalledNumber] = useState('');
  const [isCalling, setIsCalling] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const isAutoScrollPaused = useRef(false);
  const isResetting = useRef(false);

  // Auto-scroll logic
  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    let animationId: number;
    let lastTime: number;
    
    const scroll = (time: number) => {
      if (lastTime === undefined) {
        lastTime = time;
        animationId = requestAnimationFrame(scroll);
        return;
      }
      const deltaTime = time - lastTime;
      lastTime = time;

      if (!isAutoScrollPaused.current && !flippedCard && !isResetting.current) {
        // Very slow and smooth increment
        const speed = 0.03; // Adjust speed here
        const increment = speed * deltaTime;
        
        const maxScroll = scrollContainer.scrollWidth - scrollContainer.offsetWidth;
        if (maxScroll > 0) {
          if (scrollContainer.scrollLeft >= maxScroll - 1) {
            isResetting.current = true;
            scrollContainer.scrollTo({ left: 0, behavior: 'smooth' });
            setTimeout(() => {
              isResetting.current = false;
              lastTime = performance.now(); // Reset timing
            }, 1000);
          } else {
            scrollContainer.scrollLeft += increment;
          }
        }
      }
      animationId = requestAnimationFrame(scroll);
    };

    animationId = requestAnimationFrame(scroll);
    return () => cancelAnimationFrame(animationId);
  }, [activeService, flippedCard]);

  useEffect(() => {
    setFlippedCard(null);
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ left: 0, behavior: 'auto' });
    }
  }, [activeService]);

  const services = [
    { name: 'Web', icon: Globe, icon3D: Icon3DGlobe, subtitle: 'Web Apps, Portals etc', color: 'bg-blue-600' },
    { name: 'Mobile', icon: Smartphone, icon3D: Icon3DMobile, subtitle: 'iOS, Android, Hybrid', color: 'bg-pink-600' },
    { name: 'Digital Marketing', icon: Megaphone, icon3D: Icon3DMegaphone, subtitle: 'SEO, SEM, Social', color: 'bg-emerald-600' },
    { name: 'Maintainence', icon: Wrench, icon3D: Icon3DWrench, subtitle: 'Support, Security', color: 'bg-slate-700' },
    { name: 'Cloud Solutions', icon: Cloud, icon3D: Icon3DCloud, subtitle: 'AWS, Azure, Hosting', color: 'bg-amber-600' }
  ];

  const subServices: Record<string, any[]> = {
    'Web': [
      { 
        title: 'UI/UX', 
        id: 'ui-ux',
        desc: 'User Research & Design', 
        icon: Layout, 
        icon3D: Icon3DCode,
        color: 'from-pink-500 to-rose-600', 
        backContent: { 
          text: 'Delivering cutting-edge and high-quality website design services with a comprehensive usability checklist that guides our creative process.', 
          checklist: ['Navigation Flow', 'Visual Hierarchy', 'Interaction Design'] 
        } 
      },
      { title: 'Custom Apps', id: 'web', desc: 'Developing rich, user-friendly desktop applications', icon: Code, icon3D: Icon3DCode, color: 'from-blue-500 to-indigo-600', backContent: { text: 'Developing rich, user-friendly and effective desktop applications with relevant cutting-edge technology that improves performance.', checklist: ['Instant Sync', 'File System Access', 'Offline Ready', 'Synchronized Processes'] } },
      { title: 'CMS Solutions', id: 'web', desc: 'Feature-rich website management', icon: FileText, color: 'from-emerald-500 to-teal-600', backContent: { text: 'Feature-rich, time-tested applications allowing you to modify your website easily with no programming knowledge required.', checklist: ['Wordpress Experts', 'Joomla Platforms', 'PHPBB & SMF', 'Ongoing Functionality'] } },
      { title: 'ERP Solutions', id: 'web', desc: 'Portable web-based business solutions', icon: Database, color: 'from-amber-500 to-orange-600', backContent: { text: 'Web-based ERP solution for SME to handle all sorts of business calculations, entirely portable and running on any web-server.', checklist: ['PHP Architecture', 'ISP Hosting', 'PDF Reporting', 'Server-side Processing'] } },
      { title: 'E-commerce', id: 'web', desc: 'Visually appealing digital stores', icon: ShoppingCart, color: 'from-violet-500 to-purple-600', backContent: { text: 'Intuitively laid out, visually appealing, and cross-browser compatible stores that ensure your brand image is portrayed effectively.', checklist: ['e-Business Consulting', 'Fast Loading', 'Cross-browser', 'Brand Portrayal'] } },
      { title: 'Plugins Development', id: 'web', desc: 'Custom components & browser extensions', icon: Puzzle, color: 'from-indigo-400 to-blue-500', backContent: { text: 'Enhancing product functionality through custom components and plugins built using NPAPI cross-browser API.', checklist: ['Adobe Reader Plugins', 'NPAPI API', 'Component Design', 'Modular Architecture'] } }
    ],
    'Mobile': [
      { title: 'iOS Development', id: 'mobile', desc: 'Best-in-class iOS applications', icon: Apple, color: 'from-slate-700 to-slate-900', backContent: { text: 'CREATING BEST-IN-CLASS IOS APPLICATIONS THAT SURPASS COMPETITORS AND TAKE ADVANTAGE OF ADVANCED IDEVICE CAPABILITIES.', checklist: ['NATIVE SWIFT/SWIFTUI', 'DEVICE INTEGRATION', 'PERFORMANCE FOCUS', 'APP STORE EXCELLENCE'] } },
      { title: 'Android Development', id: 'mobile', desc: 'CUSTOM SOLUTIONS ON THE ANDROID PLATFORM, FROM WIDGETS TO ENTERPRISE-LEVEL APPLICATIONS WITH COMPLEX INTEGRATIONS.', icon: Smartphone, color: 'from-green-500 to-emerald-600', backContent: { text: 'CUSTOM SOLUTIONS ON THE ANDROID PLATFORM, FROM WIDGETS TO ENTERPRISE-LEVEL APPLICATIONS WITH COMPLEX INTEGRATIONS.', checklist: ['NATIVE KOTLIN/JAVA', 'WIDGET DEVELOPMENT', 'CUSTOM INTEGRATIONS', 'OPTIMIZED PERFORMANCE'] } },
      { title: 'Cross Platform', id: 'mobile', desc: 'DELIVERING VERSATILE APPLICATIONS USING HTML5, PHONEGAP, AND TITANIUM, TAILORED TO YOUR BUSINESS REQUIREMENTS.', icon: Layers, color: 'from-blue-400 to-cyan-600', backContent: { text: 'DELIVERING VERSATILE APPLICATIONS USING HTML5, PHONEGAP, AND TITANIUM, TAILORED TO YOUR BUSINESS REQUIREMENTS.', checklist: ['HTML5 FRAMEWORKS', 'PHONEGAP SOLUTIONS', 'TITANIUM DEVELOPMENT', 'VERSATILE COMPATIBILITY'] } }
    ],
    'Digital Marketing': [
      { title: 'SEO', id: 'digital-marketing', desc: 'Boosting visibility and rankings', icon: TrendingUp, color: 'from-blue-600 to-blue-800', backContent: { text: 'BOOSTING YOUR VISIBILITY WITH LOCAL SEO, STRATEGIC KEYWORD RESEARCH, AND ON-PAGE OPTIMIZATION TO RANK #1.', checklist: ['LOCAL SEO MASTERY', 'KEYWORD INTELLIGENCE', 'ON-PAGE OPTIMIZATION', 'BACKLINK STRATEGY'] } },
      { title: 'Social Media', id: 'digital-marketing', desc: 'Visual storytelling and engagement', icon: Share2, color: 'from-indigo-500 to-purple-600', backContent: { text: 'LEVERAGING VISUAL STORYTELLING ON INSTAGRAM AND PINTEREST TO CREATE A ROBUST DIGITAL PRESENCE THAT ATTRACTS LEADS.', checklist: ['BRAND STORYTELLING', 'AUDIENCE ENGAGEMENT', 'VISUAL CONTENT', 'SOCIAL MEDIA ROI'] } },
      { title: 'Paid Advertising (PPC)', id: 'digital-marketing', desc: 'High-converting ad campaigns', icon: Target, color: 'from-red-500 to-orange-600', backContent: { text: 'HIGH-CONVERTING AD CAMPAIGNS DESIGNED TO TURN CLICKS INTO QUALIFIED LEADS THROUGH GOOGLE AND META ADS.', checklist: ['LEAD GENERATION', 'ROI-FOCUSED ADS', 'A/B TESTING', 'RETARGETING CAMPAIGNS'] } },
      { title: 'Content Marketing', id: 'digital-marketing', desc: 'Establishing authority through content', icon: FileText, color: 'from-emerald-600 to-teal-700', backContent: { text: 'ESTABLISHING AUTHORITY THROUGH EDUCATIONAL BLOGS AND VIDEO TUTORIALS THAT NURTURE LEADS INTO PAYING CUSTOMERS.', checklist: ['AUTHORITY BUILDING', 'EDUCATIONAL CONTENT', 'VIDEO PRODUCTION', 'LEAD NURTURING'] } }
    ],
    'Maintainence': [
      { title: 'System Optimization', id: 'maintenance', desc: 'Peak performance & scalability', icon: Zap, color: 'from-yellow-500 to-orange-600', backContent: { text: 'Maximizing system throughput and minimizing latency through advanced performance tuning and resource management.', checklist: ['Performance Tuning', 'Resource Management', 'Latency Reduction', 'Scalability Audit'] } },
      { title: 'App Support', id: 'maintenance', desc: '24/7 dedicated maintenance', icon: Headphones, color: 'from-blue-500 to-cyan-600', backContent: { text: 'Dedicated 24/7 application support to ensure your business operations run smoothly without any technical hurdles.', checklist: ['24/7 Monitoring', 'Troubleshooting', 'SLA Management', 'Help Desk Support'] } },
      { title: 'Error Rectification', id: 'maintenance', desc: 'Proactive bug detection & fixes', icon: Bug, color: 'from-red-500 to-rose-600', backContent: { text: 'Swift detection and resolution of software bugs and glitches to maintain a secure and glitch-free environment.', checklist: ['Bug Tracking', 'Hotfix Deployment', 'Regression Testing', 'Root Cause Analysis'] } },
      { title: 'Legacy Migration', id: 'maintenance', desc: 'Seamless platform transitions', icon: ArrowUpCircle, color: 'from-indigo-500 to-violet-600', backContent: { text: 'Seamlessly transitioning your outdated systems to modern platforms with minimal risk and zero data loss.', checklist: ['Data Migration', 'Cloud Integration', 'Platform Upgrades', 'Zero-Downtime Strategy'] } },
      { title: 'Flexible Scaling', id: 'maintenance', desc: 'On-demand feature enhancements', icon: Settings2, color: 'from-emerald-500 to-teal-600', backContent: { text: 'Adaptive solutions that grow with your business, allowing for easy integration of new features and increased loads.', checklist: ['Auto-Scaling Setup', 'Feature Add-ons', 'Load Balancing', 'Elastic Infrastructure'] } },
      { title: 'Security & Speed', id: 'maintenance', desc: 'Secure high-performance infrastructure', icon: ShieldCheck, color: 'from-slate-700 to-slate-900', backContent: { text: 'Fortifying your digital assets with advanced security while maintaining lightning-fast load times and responsiveness.', checklist: ['Security Patching', 'Vulnerability Audits', 'SSL & Encryption', 'Speed Optimization'] } },
      { title: 'Quality Assurance', id: 'maintenance', desc: 'Premium results & code excellence', icon: CheckCircle, color: 'from-blue-600 to-indigo-700', backContent: { text: 'Rigorous testing protocols to ensure every release meets the highest standards of code quality and user experience.', checklist: ['Automated Testing', 'User Acceptance', 'Code Reviews', 'Performance Testing'] } },
      { title: 'Uptime Management', id: 'maintenance', desc: 'Zero downtime & peak productivity', icon: Clock, color: 'from-green-500 to-emerald-600', backContent: { text: 'Proactive maintenance strategies designed to eliminate downtime and maximize availability of critical services.', checklist: ['High Availability', 'Disaster Recovery', 'Uptime Monitoring', 'Backup Solutions'] } },
      { title: 'UX Improvement', id: 'maintenance', desc: 'Enhanced customer satisfaction', icon: Smile, color: 'from-pink-500 to-purple-600', backContent: { text: 'Continuously evolving your interface based on user feedback to drive higher engagement and satisfaction.', checklist: ['User Feedback Loop', 'Interface Refinement', 'Accessibility', 'Engagement Tracking'] } }
    ],
    'Cloud Solutions': [
      { title: 'Amazon Web Services', id: 'cloud', desc: 'Scalable cloud infrastructure', icon: Server, color: 'from-orange-400 to-orange-600', backContent: { text: 'LEVERAGING AWS FOR COMPUTE POWER, STORAGE, AND FLEXIBILITY WITHOUT UP-FRONT EXPENSES OR LONG-TERM COMMITMENTS.', checklist: ['COMPUTE & STORAGE', 'ARCHITECTURE PLANNING', 'COST BUDGETING', 'APEX EFFICIENCY'] } },
      { title: 'Google Cloud', id: 'cloud', desc: 'Modern infrastructure and tools', icon: Cloud, color: 'from-blue-400 to-blue-600', backContent: { text: 'BUILDING APPLICATIONS AND WEBSITES ON GOOGLE\'S INFRASTRUCTURE WITH INTEGRATED DEVELOPMENT TOOLS FOR ANY WORKFLOW.', checklist: ['INFRASTRUCTURE SETUP', 'DATA ANALYSIS', 'INTEGRATED ENVIRONMENT', 'SCALABLE WORKFLOWS'] } },
      { title: 'Software as a Service', id: 'cloud', desc: 'Internet-based service delivery', icon: Cpu, color: 'from-purple-500 to-indigo-600', backContent: { text: 'DELIVERY OF USER-SPECIFIC SERVICES OVER THE INTERNET, ELIMINATING THE NEED FOR LOCAL HARDWARE AND MAINTENANCE.', checklist: ['SAAS DELIVERY', 'EFFICIENCY IMPROVEMENT', 'ZERO DISASTER RECOVERY', 'COLLABORATION FOCUS'] } }
    ]
  };

  return (
    <div onClick={() => setFlippedCard(null)}>
      <main className="max-w-7xl mx-auto px-4 py-6 md:py-8">
        <div className="flex flex-col lg:grid lg:grid-cols-12 gap-6 md:gap-8">
          
          {/* Main Dashboard Area */}
          <div className="lg:col-span-8 xl:col-span-9 space-y-6 md:space-y-8">
            
            {/* Welcome & Offers */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 md:gap-8">
                <div>
                  <h1 className="sr-only">OETechs - Best UI/UX Design & Web Development Agency in Hyderabad</h1>
                  <motion.div 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="flex items-center gap-2 mb-1"
                  >
                    <div className="flex">
                      {"Welcome,".split("").map((char, i) => (
                        <motion.span
                          key={i}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ 
                            delay: i * 0.05,
                            duration: 0.5,
                            ease: "easeOut"
                          }}
                          className="text-2xl md:text-3xl font-black bg-gradient-to-r from-[#002a5c] via-blue-600 to-indigo-600 dark:from-white dark:via-blue-400 dark:to-indigo-400 bg-clip-text text-transparent"
                        >
                          {char}
                        </motion.span>
                      ))}
                    </div>
                    <motion.div
                      animate={{ 
                        scale: [1, 1.2, 1],
                        rotate: [0, 15, -15, 0],
                        filter: ["drop-shadow(0 0 0px rgba(255,204,0,0))", "drop-shadow(0 0 8px rgba(255,204,0,0.6))", "drop-shadow(0 0 0px rgba(255,204,0,0))"]
                      }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <Sparkles className="text-[#ffcc00] mb-2" size={24} />
                    </motion.div>
                  </motion.div>
                  <div className="flex items-center gap-2 animate__animated animate__fadeInLeft animate__delay-1s">
                    <motion.div 
                      className="flex items-center"
                      initial="initial"
                      animate="animate"
                    >
                      {[
                        { char: 'O', color: 'text-blue-600', shadow: 'shadow-blue-500/50' },
                        { char: 'E', color: 'text-emerald-500', shadow: 'shadow-emerald-500/50' },
                        { char: 'T', color: 'text-indigo-600', shadow: 'shadow-indigo-500/50' },
                        { char: 'e', color: 'text-rose-500', shadow: 'shadow-rose-500/50' },
                        { char: 'c', color: 'text-amber-500', shadow: 'shadow-amber-500/50' },
                        { char: 'h', color: 'text-violet-600', shadow: 'shadow-violet-500/50' },
                        { char: 's', color: 'text-cyan-500', shadow: 'shadow-cyan-500/50' }
                      ].map((item, i) => (
                        <motion.span
                          key={i}
                          animate={{ 
                            y: [0, -10, 0],
                            scale: [1, 1.2, 1],
                            filter: ["brightness(1)", "brightness(1.5)", "brightness(1)"],
                          }}
                          transition={{ 
                            duration: 1.5,
                            repeat: Infinity,
                            delay: i * 0.15,
                            ease: "easeInOut"
                          }}
                          whileHover={{ 
                            y: 5,
                            scale: 0.9,
                            transition: { type: "spring", stiffness: 400, damping: 10 }
                          }}
                          className={`text-sm md:text-base font-black tracking-tight cursor-default select-none ${item.color} drop-shadow-md inline-block px-[1px]`}
                        >
                          {item.char}
                        </motion.span>
                      ))}
                      <span className="text-xs md:text-sm text-slate-500 dark:text-slate-400 font-semibold ml-1.5 tracking-wide uppercase italic opacity-80">Services</span>
                    </motion.div>
                    <div className="flex items-center gap-1 ml-1">
                      <motion.div 
                        animate={{ 
                          scale: [1, 1.2, 1],
                          opacity: [0.5, 1, 0.5] 
                        }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.6)]"
                      />
                      <div className="h-px w-8 bg-gradient-to-r from-emerald-500/50 to-transparent" />
                    </div>
                  </div>
                </div>
              </div>

              <Link 
                to="/offers"
                className="bg-[#fff9e6] dark:bg-amber-900/20 border border-[#ffe082] dark:border-amber-500/30 px-4 py-3 rounded-2xl flex items-center gap-3 md:gap-4 cursor-pointer hover:shadow-md transition-all sm:max-w-xs animate__animated animate__swing animate__infinite animate__slow"
              >
                <div className="bg-[#ffcc00] p-2 rounded-lg shrink-0">
                  <Gift className="text-[#002a5c]" size={20} />
                </div>
                <div className="flex-1">
                  <p className="text-xs font-semibold text-[#002a5c] dark:text-amber-400">Special offers for you!</p>
                  <p className="text-[10px] text-slate-600 dark:text-slate-400 italic leading-tight">Pre-approved for small scale business</p>
                </div>
                <ChevronRight size={18} className="text-[#002a5c] dark:text-amber-400 shrink-0" />
              </Link>
            </div>

            {/* Mobile-optimized Services Grid (Show only on Mobile, below Welcome) */}
            <div className="md:hidden grid grid-cols-3 gap-1.5 mb-4">
              {services.map((item) => {
                const isActive = activeService === item.name;
                return (
                  <button 
                    key={item.name}
                    onClick={() => setActiveService(item.name)}
                    className={`flex flex-col p-1.5 rounded-xl border transition-all active:scale-95 ${
                      isActive 
                        ? `${item.color} text-white border-transparent shadow-md` 
                        : 'bg-white dark:bg-white/5 border-slate-100 dark:border-white/10 text-slate-600 dark:text-slate-400'
                    }`}
                  >
                    <div className="flex flex-col items-center text-center gap-1">
                      <div className={`p-1 rounded-lg ${isActive ? 'bg-white/20' : 'bg-slate-100 dark:bg-white/10'}`}>
                        {isActive && item.icon3D ? (
                          <item.icon3D size="w-3 h-3" />
                        ) : (
                          React.createElement(item.icon, { size: 12, className: isActive ? 'text-white' : '' })
                        )}
                      </div>
                      <span className="text-[9px] font-semibold font-inter leading-tight">{item.name}</span>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Quick Actions Tabs (Desktop Only) */}
            <div className="hidden md:flex items-center gap-3 overflow-x-auto pb-6 scrollbar-hide -mx-1 px-1">
              {services.map((item) => {
                const isActive = activeService === item.name;
                return (
                  <button 
                    key={item.name}
                    onClick={() => setActiveService(item.name)}
                    className={`relative group transition-all duration-500 ease-in-out shrink-0 ${
                      isActive 
                        ? `${item.color} text-white p-2 md:p-2.5 pr-6 md:pr-8 rounded-2xl shadow-lg` 
                        : 'bg-white/40 dark:bg-white/5 hover:bg-[#1a222c]/5 dark:hover:bg-white/10 text-slate-600 dark:text-slate-400 p-1.5 md:p-2 px-4 md:px-5 rounded-full border border-slate-200 dark:border-white/10'
                    }`}
                  >
                    <div className="flex items-center gap-2 md:gap-2.5">
                      <div className={`p-1 md:p-1.5 rounded-xl transition-colors duration-500 ${isActive ? 'bg-blue-500/20' : 'bg-slate-100'}`}>
                        {isActive && item.icon3D ? (
                          <item.icon3D size="w-4 h-4 md:w-5 md:h-5" />
                        ) : (
                          React.createElement(item.icon, { size: isActive ? 16 : 14 })
                        )}
                      </div>
                      <div className="text-left">
                        <p className={`font-semibold transition-all duration-500 ${isActive ? 'text-[10px] md:text-xs' : 'text-[9px] md:text-[10px]'}`}>
                          {item.name}
                        </p>
                        {isActive && (
                          <motion.p 
                            initial={{ opacity: 0, x: -5 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="text-[8px] md:text-[9px] text-slate-400 mt-0.5 whitespace-nowrap"
                          >
                            {item.subtitle}
                          </motion.p>
                        )}
                      </div>
                    </div>
                    {isActive && (
                      <div className="absolute bottom-0 left-4 right-4 h-1 bg-[#ffcc00] rounded-full">
                        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-[#ffcc00] rotate-45" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Main Cards Grid / Service Slider */}
            <div id="services-slider" className="relative group">
              {subServices[activeService] && (
                <div 
                  ref={scrollRef}
                  onMouseEnter={() => { isAutoScrollPaused.current = true; }}
                  onMouseLeave={() => { isAutoScrollPaused.current = false; }}
                  className="flex gap-6 overflow-x-auto pb-8 scrollbar-hide -mx-2 px-2"
                >
                  {subServices[activeService].map((sub, idx) => {
                    const isFlipped = flippedCard === sub.title;
                    return (
                      <motion.div
                        key={sub.title}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0, height: isFlipped ? 380 : 260 }}
                        transition={{ opacity: { delay: idx * 0.1 }, y: { delay: idx * 0.1 }, height: { duration: 0.4, ease: "easeInOut" } }}
                        className="min-w-[280px] md:min-w-[320px] snap-center perspective-1000 group/flip"
                      >
                        <motion.div 
                          animate={{ rotateY: isFlipped ? 180 : 0 }}
                          transition={{ duration: 0.7, ease: "easeInOut" }}
                          className="relative w-full h-full preserve-3d"
                        >
                          {/* Front */}
                          <div className={`absolute inset-0 backface-hidden bg-gradient-to-br ${sub.color} p-6 rounded-3xl shadow-xl flex flex-col group/card transition-all duration-500`}>
                            <div className="flex justify-between items-start mb-4">
                              <div className="p-3 rounded-2xl bg-white/20 text-white backdrop-blur-md shadow-lg group-hover/card:scale-110 transition-transform border border-white/20 relative z-10">
                                {sub.icon3D ? (
                                  <sub.icon3D size="w-6 h-6" />
                                ) : (
                                  React.createElement(sub.icon, { size: 24 })
                                )}
                              </div>
                              <button 
                                onClick={(e) => { e.stopPropagation(); setFlippedCard(sub.title); }}
                                className="p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors group-hover/card:translate-x-1 relative z-10"
                              >
                                <ChevronRight size={20} className="text-white" />
                              </button>
                            </div>
                            
                            <div className="relative flex-1 flex flex-col">
                              <h3 className="text-lg font-semibold text-white mb-2 relative z-10">{sub.title}</h3>
                              <p className="text-xs text-white/80 leading-relaxed relative z-10">{sub.desc}</p>
                              
                              {sub.image && (
                                <div className="mt-4 flex-1 rounded-2xl overflow-hidden border border-white/10 shadow-inner bg-black/5">
                                  <img src={sub.image} alt={sub.title} className="w-full h-full object-cover opacity-90 group-hover/card:scale-110 transition-transform duration-700" />
                                </div>
                              )}
                              
                              {/* 3D Graphic Decorations */}
                              <div className="absolute top-1/2 right-0 -translate-y-1/2 opacity-10 group-hover/card:opacity-20 transition-opacity">
                                {React.createElement(sub.icon, { size: 120, className: "animate-float" })}
                              </div>
                            </div>

                            <div className="mt-4 pt-4 flex items-center justify-between border-t border-white/10 relative z-10">
                              <div 
                                onClick={(e) => { e.stopPropagation(); setFlippedCard(sub.title); }}
                                className="flex items-center gap-2 group/view cursor-pointer"
                              >
                                <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center group-hover/view:bg-[#ffcc00] group-hover/view:text-[#002a5c] transition-all duration-300">
                                  <Eye size={14} />
                                </div>
                                <span className="text-[10px] font-semibold text-white uppercase tracking-wider group-hover/view:text-[#ffcc00] transition-colors">View Details</span>
                              </div>
                              <div className="flex -space-x-2">
                                {[1, 2, 3].map(i => (
                                  <div key={i} className="w-6 h-6 rounded-full border-2 border-white/30 bg-white/10 flex items-center justify-center overflow-hidden backdrop-blur-sm">
                                    <img src={`/logo.png`} alt="client" className="w-full h-full object-cover opacity-80" />
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>

                          {/* Back */}
                          <div 
                            onClick={(e) => e.stopPropagation()}
                            className={`absolute inset-0 backface-hidden bg-slate-900 p-6 rounded-3xl text-white shadow-xl rotate-y-180 flex flex-col border border-white/10 transition-all duration-300 z-20`}
                          >
                            <div className="flex justify-between items-start mb-4">
                              <div className="flex items-center gap-3">
                                <div className="p-2 bg-blue-500/20 rounded-lg text-blue-400">
                                  {React.createElement(sub.icon, { size: 20 })}
                                </div>
                                <h4 className="font-semibold text-lg">{sub.title}</h4>
                              </div>
                              <button 
                                onClick={(e) => { e.stopPropagation(); setFlippedCard(null); }} 
                                className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20"
                              >
                                <X size={18} />
                              </button>
                            </div>
                            <p className="text-[10px] text-slate-300 leading-relaxed mb-6">{sub.backContent.text}</p>
                            <div className="space-y-3">
                              <p className="text-[10px] font-semibold text-blue-400 uppercase tracking-widest">Service Overview</p>
                              {sub.backContent.checklist.map((item: string) => (
                                <div key={item} className="flex items-center gap-2">
                                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
                                  <span className="text-[11px] font-medium text-slate-200">{item}</span>
                                </div>
                              ))}
                            </div>
                            <div className="mt-auto space-y-3">
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate('/quote');
                                }}
                                className="w-full py-2.5 bg-[#ffcc00] text-[#002a5c] rounded-xl font-semibold text-sm hover:scale-105 transition-all flex items-center justify-center gap-2"
                              >
                                Get a Free Quote
                              </button>
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const path = `/services/${sub.id || (
                                    activeService.toLowerCase() === 'maintainence' ? 'maintenance' : 
                                    activeService.toLowerCase() === 'cloud solutions' ? 'cloud' :
                                    activeService.toLowerCase().replace(' ', '-')
                                  )}`;
                                  navigate(path);
                                }}
                                className="w-full py-2.5 bg-blue-600 text-white rounded-xl font-semibold text-sm hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 group/btn"
                              >
                                View Details
                                <ChevronRight size={18} className="group-hover/btn:translate-x-1 transition-transform" />
                              </button>
                            </div>
                          </div>
                        </motion.div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Bottom Grid: Quick Enquiry & Corporate Trainings */}
            <div id="enquiry" className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
               {/* Quick Enquiry */}
               <div className="bg-gradient-to-br from-white to-slate-50 rounded-3xl shadow-md border border-slate-200 overflow-hidden flex flex-col">
                  <div className="bg-gradient-to-r from-[#002a5c] via-[#002a5c] to-[#002a5c] p-3 relative overflow-hidden flex items-center justify-between group">
                    <div>
                      <h2 className="text-base font-semibold text-white tracking-tight">Quick Enquiry</h2>
                      <p className="text-blue-100 text-[9px] uppercase font-semibold tracking-widest mt-0.5">Instant response • Zero wait</p>
                    </div>
                    <img src="/logo.png" className="h-6 drop-shadow-[0_0_20px_rgba(255,255,255,0.8)] brightness-150" alt="logo" />
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-3xl -mr-16 -mt-16 group-hover:scale-110 transition-transform" />
                  </div>
                  <div className="p-5 md:p-6 space-y-5 flex-1 flex flex-col">
                    <div>
                      <label className="text-[10px] font-semibold text-slate-700 uppercase tracking-wide block mb-2">Choose Service</label>
                      <div className="grid grid-cols-3 gap-2.5">
                         {[
                           { label: 'Domain', icon3D: Icon3DDomain, color: 'from-indigo-50 to-indigo-100', borderColor: 'border-indigo-300' },
                           { label: 'Website', icon3D: Icon3DWebDev, color: 'from-emerald-50 to-emerald-100', borderColor: 'border-emerald-300' },
                           { label: 'Hosting', icon3D: Icon3DHosting, color: 'from-cyan-50 to-cyan-100', borderColor: 'border-cyan-300' }
                         ].map((item, i) => {
                           const isActive = activeEnquiry === item.label;
                           return (
                             <button 
                               key={item.label} 
                               onClick={() => setActiveEnquiry(item.label)}
                               className={`p-3 rounded-2xl border transition-all transform hover:scale-105 ${isActive ? `bg-gradient-to-br ${item.color} ${item.borderColor} shadow-md scale-105` : `bg-white ${item.borderColor}/40 hover:${item.borderColor}/70 text-slate-600`}`}
                             >
                               <div className="flex flex-col items-center gap-2">
                                 <item.icon3D size="w-4 h-4" />
                                 <span className={`text-[9px] font-semibold ${isActive ? 'text-slate-800' : 'text-slate-500'}`}>{item.label}</span>
                               </div>
                             </button>
                           );
                         })}
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-semibold text-slate-700 uppercase tracking-wide">Enter Mobile Number</label>
                      <div className="relative">
                        <input 
                          type="tel" 
                          placeholder="9876543210" 
                          value={mobileNumber}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                            setMobileNumber(val);
                            if (val.length === 10) {
                              const to = '+91' + val;
                              // avoid duplicate calls for same number and while a call is in progress
                              if (!isCalling && to !== lastCalledNumber) {
                                // make call via backend
                                (async () => {
                                  try {
                                    setIsCalling(true);
                                    setCallStatus('calling');
                                    const base = (process.env.REACT_APP_BACKEND_URL || '/backend').replace(/\/+$/, '');
                                    const res = await fetch(`${base}/api.php?action=make_call`, {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ to })
                                    });
                                    const data = await res.json();
                                    if (res.ok) {
                                      setCallStatus('initiated');
                                      setLastCalledNumber(to);
                                      console.log('call response', data);
                                    } else {
                                      setCallStatus('error');
                                      console.error('call error', data);
                                    }
                                  } catch (err) {
                                    setCallStatus('error');
                                    console.error('call error', err);
                                  } finally {
                                    setIsCalling(false);
                                    // clear status after a short delay
                                    setTimeout(() => setCallStatus(null), 4000);
                                  }
                                })();
                              }
                            }
                          }}
                          className="w-full border-2 border-slate-200 py-2.5 px-4 text-sm font-semibold outline-none focus:border-blue-500 transition-colors rounded-xl" 
                        />
                        {mobileNumber.length === 10 && (
                          <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
                            <div className={`w-2.5 h-2.5 rounded-full ${isCalling ? 'bg-yellow-400 animate-pulse' : (callStatus === 'initiated' ? 'bg-green-500' : (callStatus === 'error' ? 'bg-red-500' : 'bg-green-500'))}`} />
                            <p className="text-[9px] font-semibold text-slate-700">
                              {isCalling ? 'Calling...' : (callStatus === 'initiated' ? 'Call initiated' : (callStatus === 'error' ? 'Call failed' : 'Verified'))}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* New Content with useme.png */}
                    <div className="flex-1 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/10 dark:to-indigo-900/10 rounded-2xl p-4 flex flex-col items-center justify-center gap-2 border border-blue-100 dark:border-blue-800/30 overflow-hidden relative group mt-auto">
                      <div className="w-full relative h-28 flex items-center justify-center">
                        <img 
                          src="/images/useme.png" 
                          alt="Expert Consultation" 
                          className="w-full h-full object-contain scale-[2.8] transform translate-y-3 mix-blend-multiply dark:mix-blend-normal opacity-90 transition-transform group-hover:scale-[2.9]"
                        />
                      </div>
                      <div className="text-center z-10">
                        <h4 className="text-[11px] font-semibold text-blue-700 dark:text-blue-400 uppercase tracking-wider">Strategic Solutions</h4>
                        <p className="text-[9px] text-slate-600 dark:text-slate-400 mt-0.5 font-medium leading-tight">Elevate your digital presence with our world-class expertise and 24/7 priority support.</p>
                      </div>
                    </div>
                  </div>
               </div>

               {/* Corporate Trainings */}
               <div id="trainings" className="bg-gradient-to-br from-white to-slate-50 rounded-3xl shadow-md border border-slate-200 overflow-hidden flex flex-col">
                  <div className="bg-gradient-to-r from-purple-600 via-violet-600 to-indigo-700 p-3 relative overflow-hidden flex items-center justify-between group">
                    <div>
                      <h2 className="text-base font-semibold text-white tracking-tight">Corporate Trainings</h2>
                      <p className="text-purple-100 text-[9px] uppercase font-semibold tracking-widest mt-0.5">AI Enhanced • Skill Boosting</p>
                    </div>
                    <Cpu size={24} className="text-purple-300 animate-pulse drop-shadow-lg" />
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-3xl -mr-16 -mt-16 group-hover:scale-110 transition-transform" />
                  </div>
                  <div className="p-5 md:p-6 space-y-5">
                    <div className="space-y-3">
                      {[
                        { id: 'fullstack', title: 'FullStack Development', desc: 'Html, CSS, Js, React, Node, MongoDB', icon3D: Icon3DFullStack, bgGradient: 'from-emerald-50 to-emerald-100', borderColor: 'border-emerald-300', color: 'text-emerald-700' },
                        { id: 'uiux', title: 'UI/UX Design', desc: 'Angular, React, Vue, JS, CSS, Design Systems', icon3D: Icon3DUITraining, bgGradient: 'from-pink-50 to-pink-100', borderColor: 'border-pink-300', color: 'text-pink-700' },
                        { id: 'backend', title: 'Backend Development', desc: 'Python, PHP, Java, ML, API, Databases', icon3D: Icon3DBackendTraining, bgGradient: 'from-blue-50 to-blue-100', borderColor: 'border-blue-300', color: 'text-blue-700' }
                      ].map((link, i) => (
                        <button 
                          key={i} 
                          onClick={() => navigate(`/trainings/${link.id}`, { state: { courseTitle: link.title } })}
                          className={`w-full flex items-center gap-3 p-3 bg-gradient-to-br ${link.bgGradient} rounded-2xl border ${link.borderColor} group hover:shadow-md transition-all`}
                        >
                          <div className={`w-10 h-10 md:w-12 md:h-12 bg-white ${link.color} rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-md border ${link.borderColor}/30`}>
                            <link.icon3D size="w-5 h-5" />
                          </div>
                          <div className="flex-1 text-left">
                            <h4 className={`text-xs font-semibold ${link.color} flex items-center gap-2 group-hover:gap-2.5 transition-all`}>{link.title} <ChevronRight size={14} className="opacity-0 group-hover:opacity-100 transition-all" /></h4>
                            <p className="text-[9px] md:text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">{link.desc}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                    
                    <div className="pt-3 border-t border-slate-200">
                      <a 
                        href="tel:9849231222" 
                        className="w-full flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-purple-600 via-violet-600 to-indigo-700 text-white rounded-2xl font-semibold shadow-lg hover:shadow-purple-500/30 hover:scale-[1.02] transition-all group text-sm"
                      >
                        <PhoneCall size={16} className="animate-bounce" />
                        <span>Enquiry Now</span>
                        <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                      </a>
                    </div>
                  </div>
               </div>
            </div>

            {/* Core Values Section */}
            <div className="bg-gradient-to-br from-white via-slate-50 to-white dark:from-[#001a35] dark:to-[#0f172a] rounded-3xl p-4 md:p-6 shadow-md border border-slate-200 dark:border-slate-700 transition-colors">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-blue-50 dark:from-blue-900/30 dark:to-blue-900/10 text-blue-600 dark:text-blue-400 rounded-xl flex items-center justify-center animate__animated animate__bounceIn shadow-md">
                  <ShieldCheck size={20} />
                </div>
                <div>
                  <h2 className="text-base font-semibold bg-gradient-to-r from-[#002a5c] to-[#002a5c] bg-clip-text text-transparent animate__animated animate__fadeInRight">Our Core Values</h2>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">Built on trust, innovation & excellence</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { title: 'Strategic Partnerships', desc: 'We treat every client as a long-term partner, regardless of scale. Our commitment is to deliver excellence that fosters mutual growth and a win-win synergy.', icon3D: Icon3DPartnership, bgGradient: 'from-blue-50 to-blue-100', borderColor: 'border-blue-300', color: 'text-blue-700' },
                  { title: 'Radical Transparency', desc: 'We involve our partners in every phase of development. Through informative consultations and a systematic service approach, we ensure you are always in the loop.', icon3D: Icon3DTransparency, bgGradient: 'from-emerald-50 to-emerald-100', borderColor: 'border-emerald-300', color: 'text-emerald-700' },
                  { title: 'Innovative Thinking', desc: 'By leveraging deep technical insights, we think outside the box to engineer bespoke strategies that align with your vision and budget.', icon3D: Icon3DInnovation, bgGradient: 'from-amber-50 to-amber-100', borderColor: 'border-amber-300', color: 'text-amber-700' },
                  { title: 'Lean & Efficient', desc: 'We prioritize streamlined solutions that eliminate waste. By reusing proven architectures and data-driven decisions, we focus on delivering maximum value.', icon3D: Icon3DEfficiency, bgGradient: 'from-indigo-50 to-indigo-100', borderColor: 'border-indigo-300', color: 'text-indigo-700' }
                ].map((value, i) => (
                  <motion.div key={i} whileHover={{ x: 5, y: -2 }} className={`flex gap-3 p-4 rounded-2xl bg-gradient-to-br ${value.bgGradient} hover:shadow-lg dark:bg-white/5 transition-all border ${value.borderColor} dark:border-white/10 group cursor-pointer`}>
                    <div className={`w-12 h-12 bg-white dark:bg-white/10 ${value.color} rounded-2xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform shadow-md border ${value.borderColor}/30`}>
                      <value.icon3D size="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className={`font-semibold ${value.color} dark:text-white mb-1 text-xs`}>{value.title}</h4>
                      <p className="text-[10px] text-slate-600 dark:text-slate-400 leading-relaxed">{value.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4 xl:col-span-3 space-y-6 md:space-y-8">
            <div id="offers" className="bg-gradient-to-br from-slate-50 to-white rounded-3xl shadow-md border border-slate-200 overflow-hidden">
               <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-4 relative overflow-hidden flex items-center justify-between group">
                 <div>
                   <h3 className="font-semibold text-white tracking-tight text-lg animate__animated animate__swing animate__infinite animate__slower">Current Offers</h3>
                   <p className="text-purple-100 text-[10px] font-semibold mt-0.5">Limited time deals</p>
                 </div>
                 <Tag size={20} className="text-yellow-300 animate__animated animate__pulse animate__infinite drop-shadow-lg" />
               </div>
               <div className="p-3 space-y-2">
                 {[
                   { name: 'Domain Registration', icon3D: Icon3DDomain, color: 'from-indigo-50 to-indigo-100', textColor: 'text-indigo-700', hoverColor: 'hover:from-indigo-100 hover:to-indigo-200', borderColor: 'border-indigo-300', accentColor: 'text-indigo-600' },
                   { name: 'Official Emails', icon3D: Icon3DEmail, color: 'from-pink-50 to-pink-100', textColor: 'text-pink-700', hoverColor: 'hover:from-pink-100 hover:to-pink-200', borderColor: 'border-pink-300', accentColor: 'text-pink-600' },
                   { name: 'Hosting', icon3D: Icon3DHosting, color: 'from-cyan-50 to-cyan-100', textColor: 'text-cyan-700', hoverColor: 'hover:from-cyan-100 hover:to-cyan-200', borderColor: 'border-cyan-300', accentColor: 'text-cyan-600' },
                   { name: 'Web Development', icon3D: Icon3DWebDev, color: 'from-emerald-50 to-emerald-100', textColor: 'text-emerald-700', hoverColor: 'hover:from-emerald-100 hover:to-emerald-200', borderColor: 'border-emerald-300', accentColor: 'text-emerald-600' },
                   { name: 'Mobile Development', icon3D: Icon3DMobileDev, color: 'from-violet-50 to-violet-100', textColor: 'text-violet-700', hoverColor: 'hover:from-violet-100 hover:to-violet-200', borderColor: 'border-violet-300', accentColor: 'text-violet-600' }
                 ].map((offer, i) => (
                   <Link 
                     key={i} 
                     to="/offers"
                     className={`w-full flex items-center justify-between p-3 md:p-4 bg-gradient-to-r ${offer.color} ${offer.hoverColor} rounded-2xl transition-all text-left group border ${offer.borderColor} shadow-sm animate__animated animate__zoomIn animate__slower`}
                     style={{ animationDelay: `${i * 0.1}s` }}
                   >
                     <div className="flex items-center gap-3">
                       <div className={`p-2.5 bg-white rounded-xl group-hover:scale-110 transition-transform duration-300 shadow-md border ${offer.borderColor}`}>
                         <offer.icon3D size="w-5 h-5" />
                       </div>
                       <span className={`text-xs md:text-sm font-semibold ${offer.textColor} group-hover:${offer.accentColor} truncate transition-colors`}>{offer.name}</span>
                     </div>
                     <ChevronRight size={18} className={`${offer.accentColor} opacity-0 group-hover:opacity-100 transition-all group-hover:translate-x-1 shrink-0`} />
                   </Link>
                 ))}
               </div>
            </div>

            <div className="bg-[#002a5c] p-6 md:p-8 rounded-3xl text-white relative overflow-hidden group">
               <div className="relative z-10">
                 <h3 className="text-xl font-semibold mb-2">Secure Your Apps</h3>
                 <p className="text-xs text-blue-200 mb-6 leading-relaxed">Protect your business with our enterprise-grade cybersecurity solutions.</p>
                 <Link to="/services/security" className="inline-block px-6 py-2.5 bg-[#ffcc00] text-[#002a5c] rounded-xl font-semibold text-xs hover:scale-105 transition-transform">Explore Now</Link>
               </div>
               <ShieldCheck className="absolute -right-4 -bottom-4 w-24 h-24 md:w-32 md:h-32 text-white/5" />
            </div>

            <div id="clients" className="bg-gradient-to-br from-white to-slate-50 rounded-3xl shadow-md border border-slate-200 overflow-hidden flex flex-col">
              <div className="bg-gradient-to-r from-teal-600 to-emerald-600 p-4 flex items-center justify-between group">
                <div>
                  <h2 className="text-lg font-semibold text-white tracking-tight">Valued Clients</h2>
                  <p className="text-teal-100 text-[9px] uppercase font-semibold tracking-widest mt-1">45+ Global Partners</p>
                </div>
                <div className="p-2.5 bg-white/20 rounded-xl group-hover:scale-110 transition-transform">
                  <Users size={20} className="text-white" />
                </div>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-2 gap-x-4 gap-y-8">
                {[
                  { name: 'IIET', desc: 'Education', logo: '/client/iiet.jpg' },
                  { name: 'KVJ', desc: 'Live Events', logo: '/client/kvj.png' },
                  { name: 'Sree Infra', desc: 'Real Estate', logo: '/client/sreeInfra.png' },
                  { name: 'Esteem', desc: 'Interior Design', logo: '/client/esteem.png' },
                  { name: 'Vspace', desc: 'Software', logo: '/client/vspace1.png' },
                  { name: 'Hako', desc: 'Kitchens', icon: ShieldCheck },
                ].map((client, i) => (
                  <div key={i} className="relative group cursor-pointer">
                    <div className="flex flex-col h-full items-center text-center">
                      <div className="mb-3 p-3 rounded-2xl shadow-lg border border-slate-200 group-hover:border-emerald-400 group-hover:shadow-emerald-200 group-hover:scale-110 transition-all duration-500 bg-white">
                        {client.logo ? (
                          <img src={client.logo} alt={client.name} className="h-14 w-14 object-contain brightness-110" />
                        ) : (
                          <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 text-white rounded-lg flex items-center justify-center">
                            {client.icon && React.createElement(client.icon, { size: 24 })}
                          </div>
                        )}
                      </div>
                      <h4 className="text-xs font-semibold text-slate-800 dark:text-white truncate">{client.name}</h4>
                      <p className="text-[9px] text-slate-500 dark:text-slate-400 truncate">{client.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Home;
